import React, { useCallback, useRef } from 'react';

import { Box } from '@opendoor/atoms/box';
import { Form } from '@opendoor/atoms/form';
import { useForm } from '@tanstack/react-form';
import { z } from 'zod';

import { useAnalyticsPrefix, useAuthActions } from '../../AuthContext';
import { GENERIC_AUTH_FAILURE_MESSAGE } from '../../errors';
import type { LastUsedMethod } from '../../lastUsedWebAuthMethod';
import { RESET_PASSWORD_STEP, useAccountForm } from '../AccountFormContext';
import { humanStatusToStep } from './humanStatus';
import { LastUsedAffordance } from './LastUsedAffordance';
import { PASSWORD_LINK_PRIMARY_STEP } from './PasswordModule';
import { readEmailFromQueryParam } from './queryPrefill';

const ANALYTICS_COMPONENT = 'account-form';

/**
 * The methods this module's CTA serves. Neither has a button of its own, so
 * both badge here.
 */
const EMAIL_METHODS: readonly LastUsedMethod[] = ['email-password', 'magic-link'];

const emailSchema = z.object({ email: z.email() });

/**
 * The V2 entry step: labeled email field, submit-pending state while a
 * human-status lookup is in flight, and routing to the step the lookup
 * result implies. Usable standalone under a bare `AccountFormProvider` or as
 * `AccountFormV2`'s default module.
 *
 * A failed lookup is published to the shared `formError` rather than rendered
 * here: the design puts the entry screen's alert region above the whole method
 * stack, which is the composition's ground, not this module's. A composition
 * that renders no region shows the user nothing, so pair the two.
 */
export const EmailModule: React.FC = () => {
  const { email, setEmail, setStep, onError, setFormError, onExistingPasswordEmailContinue } =
    useAccountForm();
  const { requestHumanStatus } = useAuthActions();
  // Set when "Forgot your password?" wins while Continue is still awaiting the
  // existing-password experiment. The late setStep must not replace that step.
  const resetPasswordChosenRef = useRef(false);
  const analyticsPrefix = useAnalyticsPrefix();
  const analyticsName = (action: string): string =>
    `${analyticsPrefix}-${ANALYTICS_COMPONENT}-${action}`;

  const form = useForm({
    defaultValues: { email: email || readEmailFromQueryParam() },
    validators: { onChange: emailSchema },
    // A submit that fails client validation skips onSubmit — clear a prior
    // server failure so its message doesn't linger beside the new field error.
    onSubmitInvalid: () => setFormError(null),
    onSubmit: async ({ value }) => {
      resetPasswordChosenRef.current = false;
      setFormError(null);
      setEmail(value.email);
      const result = await requestHumanStatus(value.email);
      if (!result.success) {
        // Surface the failure in the shared error region (the primary UX) and
        // notify the shell's onError side channel too — additive, matching
        // PasswordModule — so a consumer that wires onError for observability
        // still shows the user an error rather than a silent dead-end.
        setFormError(GENERIC_AUTH_FAILURE_MESSAGE);
        onError?.(result.error);
        return;
      }
      const nextStep = humanStatusToStep(result.data);
      if (nextStep === 'password' && onExistingPasswordEmailContinue) {
        let usePasswordless = false;
        try {
          usePasswordless = await onExistingPasswordEmailContinue();
        } catch {
          usePasswordless = false;
        }
        if (resetPasswordChosenRef.current) {
          return;
        }
        setStep(usePasswordless ? PASSWORD_LINK_PRIMARY_STEP : 'password');
        return;
      }
      setStep(nextStep);
    },
  });

  // Publish the field before leaving so the reset step targets the address on
  // screen, not its own `?email=` fallback. Only a valid address: a partial or
  // whitespace entry would otherwise overwrite a correct server-supplied prefill
  // with junk, and clearing the field is left alone deliberately.
  const goToResetPassword = useCallback(() => {
    const typed = form.getFieldValue('email')?.trim();
    if (typed && emailSchema.safeParse({ email: typed }).success) {
      setEmail(typed);
    }
    resetPasswordChosenRef.current = true;
    setStep(RESET_PASSWORD_STEP);
  }, [form, setEmail, setStep]);

  return (
    // noValidate: without it, the browser's native type="email" constraint
    // validation blocks the submit event before this schema ever runs, so an
    // invalid address shows the browser's own tooltip instead of the error text
    // below the field.
    <Form.Root
      form={form}
      noValidate
      scrollToFirstError={false}
      className="flex w-full flex-col gap-4"
    >
      <Form.Input
        name="email"
        label="Email"
        type="email"
        autoComplete="email"
        placeholder="Enter your email here"
        analyticsName={analyticsName('email')}
      />
      {/* Both email methods badge here rather than on the field: neither has a
          button of its own, and the anchor's geometry is built for a full-width
          control, not a labelled input. */}
      <LastUsedAffordance analyticsComponent={ANALYTICS_COMPONENT} methods={EMAIL_METHODS}>
        {(association) => (
          <Form.SubmitButton
            className="w-full"
            aria-label="Continue with email"
            analyticsName={analyticsName('continue')}
            {...association}
          >
            Continue with email
          </Form.SubmitButton>
        )}
      </LastUsedAffordance>
      <Box
        onClick={goToResetPassword}
        aria-label="Forgot your password?"
        analyticsName={analyticsName('forgot-password')}
        className="self-start text-label-md text-od-content-accent-brand hover:underline"
      >
        Forgot your password?
      </Box>
    </Form.Root>
  );
};
